import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1635650804263-1a1941e14df5?crop=entropy&cs=srgb&fm=jpg&q=85";

const RevealLine = ({ children, delay = 0 }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 140]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden pt-16">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute left-1/4 top-0 h-full w-px bg-[#E4E4DE]/60" />
        <div className="absolute left-2/4 top-0 h-full w-px bg-[#E4E4DE]/60" />
        <div className="absolute left-3/4 top-0 h-full w-px bg-[#E4E4DE]/60" />
      </div>
      <div className="relative max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 w-full grid lg:grid-cols-12 gap-12 items-center py-20">
        <div className="lg:col-span-7">
          <motion.p
            data-testid="hero-eyebrow"
            className="font-mono text-xs uppercase tracking-[0.25em] text-[#4D7C0F]/90"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.1 }}
          >
            Drop 004 — Heavyweight Series
          </motion.p>
          <h1 className="font-display font-extrabold uppercase tracking-tighter leading-[0.9] text-[clamp(1.7rem,6.5vw,2.9rem)] mt-6">
            <RevealLine delay={0.2}>Heavyweight</RevealLine>
            <RevealLine delay={0.35}>tees, engineered</RevealLine>
            <RevealLine delay={0.5}>
              <span className="text-[#4D7C0F]">like architecture.</span>
            </RevealLine>
          </h1>
          <motion.p
            className="mt-8 max-w-md text-sm sm:text-base text-zinc-500 leading-relaxed"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.75 }}
          >
            280GSM organic cotton. Boxy architectural cuts. Archival prints that age like
            concrete. Built in small runs — never restocked twice.
          </motion.p>
          <motion.div
            className="mt-10 flex flex-wrap gap-4"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9 }}
          >
            <Link
              to="/shop"
              data-testid="hero-shop-cta"
              className="inline-flex items-center gap-2 bg-[#C8FF1E] text-black font-mono text-xs font-bold uppercase tracking-[0.2em] px-8 py-4 hover:bg-[#B0E600] hover:text-black transition-colors"
            >
              Explore Drop 004 <ArrowRight size={16} />
            </Link>
            <Link
              to="/about"
              data-testid="hero-manifesto-cta"
              className="inline-flex items-center gap-2 border border-[#E4E4DE] text-zinc-700 font-mono text-xs uppercase tracking-[0.2em] px-8 py-4 hover:border-[#C8FF1E] hover:text-[#4D7C0F] transition-colors"
            >
              Our Manifesto
            </Link>
          </motion.div>
          <motion.div
            className="mt-14 flex gap-8 font-mono text-xs uppercase tracking-wider text-zinc-500"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.1 }}
          >
            <span data-testid="hero-stat-gsm">280 GSM fabric</span>
            <span data-testid="hero-stat-organic">100% organic</span>
            <span data-testid="hero-stat-shipping">Ships in 48h</span>
          </motion.div>
        </div>
        <motion.div
          className="lg:col-span-5"
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.2, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
        >
          <motion.div style={{ y }} className="relative border border-[#E4E4DE]">
            <img
              src={HERO_IMG}
              alt="Kinetik Archive heavyweight tee on model"
              className="w-full aspect-[3/4] object-cover grayscale hover:grayscale-0 transition-[filter] duration-700"
            />
            <div className="absolute bottom-0 inset-x-0 flex justify-between items-center bg-[#F7F7F2]/80 backdrop-blur px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-zinc-500">
              <span>MD-004 / On-body study</span>
              <span className="text-[#4D7C0F]">In stock</span>
            </div>
            <div className="absolute -top-3 -left-3 h-6 w-6 border-t-2 border-l-2 border-[#C8FF1E]" />
            <div className="absolute -bottom-3 -right-3 h-6 w-6 border-b-2 border-r-2 border-[#C8FF1E]" />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
